import React from "react";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Forms } from "@/types/form";
import { getRoutes, handleErrorToast } from "@/utils/helpers";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FetchError } from "@medusajs/js-sdk";
import { sdk } from "@/config/medusa";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import schemas from "@/utils/schemas";
import { zodResolver } from "@hookform/resolvers/zod";

const VENDOR_ACTOR_TYPES = ["vendor-admin", "vendor-accountant"] as const;

const ResetPassword: React.FC = () => {
  const form = useForm<Forms["ResetPassword"]>({
    resolver: zodResolver(schemas.resetPassword),
    defaultValues: {
      newPassword: "",
      repeatPassword: "",
    },
  });

  const { control, handleSubmit } = form;

  const navigate = useNavigate();
  const routes = getRoutes();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const typeParam = searchParams.get("type");
  const actorType =
    VENDOR_ACTOR_TYPES.find((type) => type === typeParam) ?? "vendor-admin";

  const onSubmit = async (data: Forms["ResetPassword"]) => {
    const { newPassword } = data;

    if (!token) {
      toast.error("Token is missing.");
      return;
    }

    try {
      await sdk.auth.updateProvider(
        actorType,
        "emailpass",
        {
          password: newPassword,
        },
        token
      );

      toast.success("Your password has been successfully reset.");
      navigate(routes.signIn);
    } catch (error) {
      if (error instanceof FetchError && error.status === 401) {
        toast.error(
          "This reset link has expired or is invalid. Please request a new one."
        );
        navigate(routes.forgotPassword);
      } else {
        handleErrorToast(error);
      }
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form
        id="reset-password-form"
        onSubmit={handleSubmit(onSubmit)}
        className="mb-6 flex flex-col gap-4"
      >
        <FormField
          name="newPassword"
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  type="password"
                  placeholder="New password"
                  className="w-full px-3 py-2 border rounded"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="repeatPassword"
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  type="password"
                  placeholder="Repeat password"
                  className="w-full px-3 py-2 border rounded"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default ResetPassword;
